<script setup>
import AppLayout from '@/layouts/AppLayout.vue'

defineProps({
  client:                  { type: Object, default: null },
  type_partie_estimations: { type: Array,  default: () => [] },
  configuration_imports:   { type: Array,  default: () => [] },
  configuration_modules:   { type: Array,  default: () => [] },
  labels:                  { type: Object, default: () => ({ client: {}, tpe: {}, ci: {}, cm: {} }) },
  urls:                    { type: Object, default: () => ({}) },
})

// true si v ∈ { true, 'true', 't', 1, '1' }
// false si v ∈ { false, 'false', 'f', 0, '0' }
// null sinon (affichage texte brut)
function boolish(v) {
  if (v === true  || v === 'true'  || v === 't' || v === 1 || v === '1') return true
  if (v === false || v === 'false' || v === 'f' || v === 0 || v === '0') return false
  return null
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Configuration
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-6xl mx-auto space-y-6">

        <!-- Page Header -->
        <div class="mb-2 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              Configuration client
            </h1>
            <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">
              {{ client?.nom || 'Aucun client actif' }}
            </p>
          </div>
        </div>

        <!-- Sous-navigation (5 onglets) -->
        <nav class="flex items-center gap-1 bg-surface-container-low rounded-md p-1 border border-outline-variant/10">
          <a
            href="/administration"
            class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-primary bg-surface-container rounded-sm border-l-4 border-tertiary"
          >
            Liste
          </a>
          <a :href="urls.edit_client" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Client
          </a>
          <a :href="urls.edit_logos" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Logos
          </a>
          <a :href="urls.edit_bordereau" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Bordereau
          </a>
          <a :href="urls.edit_importation" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Importation
          </a>
          <a :href="urls.edit_module" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Modules
          </a>
        </nav>

        <!-- Widget 1 : Détails du client -->
        <section class="bg-surface-container rounded-xl border-l-4 border-primary shadow-2xl shadow-black/40 overflow-hidden">
          <header class="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10">
            <h2 class="text-sm font-black text-on-surface uppercase tracking-widest">
              Détails du client
            </h2>
            <a
              :href="urls.edit_client"
              class="flex items-center gap-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
            >
              <span class="material-symbols-outlined text-sm">edit</span>
              Modifier
            </a>
          </header>

          <div v-if="client" class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-outline-variant/10">
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.client.nom }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.client.address }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.client.schema }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.client.subdomain }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.client.locale }}</th>
                </tr>
              </thead>
              <tbody>
                <tr class="hover:bg-surface-container-high/30 transition-colors">
                  <td class="px-6 py-3 font-bold text-on-surface">{{ client.nom || '—' }}</td>
                  <td class="px-6 py-3 text-on-surface">{{ client.address || '—' }}</td>
                  <td class="px-6 py-3">
                    <span class="inline-flex items-center px-2 py-0.5 rounded bg-primary/10 border border-primary/20 text-[10px] font-black uppercase tracking-widest text-primary">
                      {{ client.schema || '—' }}
                    </span>
                  </td>
                  <td class="px-6 py-3 font-mono text-primary">{{ client.subdomain || '—' }}</td>
                  <td class="px-6 py-3 text-on-surface">{{ client.locale || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="px-6 py-8 text-center">
            <span class="material-symbols-outlined text-4xl text-outline/40 mb-2">domain_disabled</span>
            <p class="text-sm text-outline">Aucun client associé à ce sous-domaine.</p>
            <p class="text-xs text-outline/60 mt-1">Accédez à la plateforme via le sous-domaine d'un client pour gérer sa configuration.</p>
            <a
              href="/clients"
              class="inline-flex items-center gap-2 mt-4 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
            >
              <span class="material-symbols-outlined text-sm">list</span>
              Voir la liste des clients
            </a>
          </div>
        </section>

        <!-- Widget 2 : Configuration du bordereau -->
        <section class="bg-surface-container rounded-xl border-l-4 border-primary shadow-2xl shadow-black/40 overflow-hidden">
          <header class="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10">
            <h2 class="text-sm font-black text-on-surface uppercase tracking-widest">
              Configuration du bordereau
            </h2>
            <a
              :href="urls.edit_bordereau"
              class="flex items-center gap-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
            >
              <span class="material-symbols-outlined text-sm">edit</span>
              Modifier
            </a>
          </header>

          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-outline-variant/10">
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.tpe.code }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.tpe.description }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.tpe.ordre }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.tpe.import_file_column_name }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.tpe.dependant }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.tpe.suivre }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="type_partie_estimations.length === 0">
                  <td colspan="6" class="px-6 py-6 text-center text-sm text-outline/60">
                    Aucune configuration de bordereau.
                  </td>
                </tr>
                <tr
                  v-for="tpe in type_partie_estimations"
                  :key="tpe.id"
                  class="border-b border-outline-variant/5 hover:bg-surface-container-high/30 transition-colors"
                >
                  <td class="px-6 py-3 font-bold text-on-surface">{{ tpe.code || '—' }}</td>
                  <td class="px-6 py-3 text-on-surface">{{ tpe.description || '—' }}</td>
                  <td class="px-6 py-3 text-outline">{{ tpe.ordre ?? '—' }}</td>
                  <td class="px-6 py-3 text-outline">{{ tpe.import_file_column_name || '—' }}</td>
                  <td class="px-6 py-3 text-outline">{{ tpe.dependent || '—' }}</td>
                  <td class="px-6 py-3">
                    <span v-if="tpe.suivre" class="material-symbols-outlined text-primary text-lg">check_circle</span>
                    <span v-else class="material-symbols-outlined text-outline/40 text-lg">cancel</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Widget 3 : Configuration de l'importation -->
        <section class="bg-surface-container rounded-xl border-l-4 border-primary shadow-2xl shadow-black/40 overflow-hidden">
          <header class="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10">
            <h2 class="text-sm font-black text-on-surface uppercase tracking-widest">
              Configuration de l'importation
            </h2>
            <a
              :href="urls.edit_importation"
              class="flex items-center gap-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
            >
              <span class="material-symbols-outlined text-sm">edit</span>
              Modifier
            </a>
          </header>

          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-outline-variant/10">
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.ci.import_model_name }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.ci.import_model_column_name }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.ci.import_file_column_name }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.ci.import_file_column_mandatory }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="configuration_imports.length === 0">
                  <td colspan="4" class="px-6 py-6 text-center text-sm text-outline/60">
                    Aucune configuration d'importation.
                  </td>
                </tr>
                <tr
                  v-for="ci in configuration_imports"
                  :key="ci.id"
                  class="border-b border-outline-variant/5 hover:bg-surface-container-high/30 transition-colors"
                >
                  <td class="px-6 py-3 font-bold text-on-surface">{{ ci.import_model_name || '—' }}</td>
                  <td class="px-6 py-3 font-mono text-sm text-outline">{{ ci.import_model_column_name || '—' }}</td>
                  <td class="px-6 py-3 text-on-surface">{{ ci.import_file_column_name || '—' }}</td>
                  <td class="px-6 py-3">
                    <span v-if="ci.import_file_column_mandatory" class="material-symbols-outlined text-primary text-lg">check_circle</span>
                    <span v-else class="material-symbols-outlined text-outline/40 text-lg">cancel</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Widget 4 : Configuration des modules -->
        <section class="bg-surface-container rounded-xl border-l-4 border-primary shadow-2xl shadow-black/40 overflow-hidden">
          <header class="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10">
            <h2 class="text-sm font-black text-on-surface uppercase tracking-widest">
              Configuration des modules
            </h2>
            <a
              :href="urls.edit_module"
              class="flex items-center gap-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
            >
              <span class="material-symbols-outlined text-sm">edit</span>
              Modifier
            </a>
          </header>

          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-outline-variant/10">
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.cm.module }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.cm.attribut }}</th>
                  <th class="text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.cm.valeur }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="configuration_modules.length === 0">
                  <td colspan="3" class="px-6 py-6 text-center text-sm text-outline/60">
                    Aucun module configuré.
                  </td>
                </tr>
                <tr
                  v-for="cm in configuration_modules"
                  :key="cm.id"
                  class="border-b border-outline-variant/5 hover:bg-surface-container-high/30 transition-colors"
                >
                  <td class="px-6 py-3 font-bold text-on-surface">{{ cm.module || '—' }}</td>
                  <td class="px-6 py-3 text-outline">{{ cm.attribut || '—' }}</td>
                  <td class="px-6 py-3">
                    <span v-if="boolish(cm.valeur) === true" class="material-symbols-outlined text-primary text-lg">check_circle</span>
                    <span v-else-if="boolish(cm.valeur) === false" class="material-symbols-outlined text-outline/40 text-lg">cancel</span>
                    <span v-else class="text-on-surface">{{ cm.valeur ?? '—' }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
